import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'
import type { Menu } from '@/api/login/types'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: route.meta
    }
    if (route.component) {
      const comModule = modules[`../${route.component}.vue`] || modules[`../${route.component}.tsx`]
      const component = route.component as string
      if (!comModule && !component.includes('#')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`)
      } else {
        // 动态加载路由文件，可根据实际情况进行自定义逻辑
        data.component =
          component === '#' ? Layout : component.includes('##') ? getParentLayout() : comModule
      }
    }
    // recursive child routes
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}

/**
 * TODO: 菜单排序，动态icon
 * 从菜单列表中提取所有组件路径
 * @param menus 菜单列表
 * @returns 组件路径数组
 */
export const getStringMenuList = (menus: Menu[]): string[] => {
  const result: string[] = []

  function traverse(menuList: Menu[], parentPath = '') {
    for (const item of menuList) {
      // 先添加当前菜单的 component（如果存在）
      if (item.component) {
        // 构建当前菜单的完整路径
        const currentPath = parentPath
          ? `${parentPath}/${item.component.replace(/^\//, '')}`
          : `/${item.component.replace(/^\//, '')}`

        result.push(currentPath)

        // 递归处理子菜单，传递当前构建的路径作为新的父路径
        if (item.children && Array.isArray(item.children)) {
          traverse(item.children, currentPath)
        }
      } else {
        // 即使当前菜单没有 component，也要继续处理子菜单
        if (item.children && Array.isArray(item.children)) {
          traverse(item.children, parentPath)
        }
      }
    }
  }

  traverse(menus)

  return result
}

// 工具函数：路径转 PascalCase 名称（用于路由 name）
function compToName(path: string): string {
  return path
    .split('/')
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')
}

// 最小可测试模块
/**
 * @description - 工具函数：同级菜单按照升序排序；数据清洗，过滤掉null/undefined/空字符串的菜单项，
 * @param menus - 菜单列表
 * @returns - 排序后的菜单列表
 */
export const sortMenus = (menus: Menu[]): Menu[] => {
  if (menus.length === 0) return []
  return menus
    .filter((menu): menu is Menu => menu !== null && menu !== undefined)
    .sort((a, b) => a.sort - b.sort)
}

/**
 * @description - 工具函数：将菜单路径转换为 PascalCase 路径, 可以做为组件路径和路由name
 * @param componentPath - 菜单路径
 * @returns - 转换后的 PascalCase 路径
 */
function convertToPascalCasePath(componentPath: string): string {
  const segments = componentPath
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      if (segment.includes('-')) {
        const segs = segment.split('-')
        return segs.map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1)).join('')
      }
      return segment
    })
  return segments.map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1)).join('/')
}

/**
 * @description - 工具函数：判断菜单是否有子菜单
 * @param menu - 菜单
 * @returns - 是否有子菜单
 */
const hasChild = (menu: Menu): boolean => {
  return !!menu.children && menu.children.length > 0
}

const getBtnPermission = (menus: Menu[]): string[] => {
  const permission: string[] = []
  if (menus.length > 0) {
    menus.forEach((item) => {
      if (item.name) {
        permission.push(item.name)
      }
    })
  }
  return permission
}

/**
 * @description - 主转换函数：将Menu[]转换为AppCustomRouteRecordRaw[]
 * @param menus - 菜单列表
 * @returns - AppCustomRouteRecordRaw[] 路由记录数组
 */
export function transformMenuToRoute(
  menus: Menu[],
  level = 0,
  parentComponentPath = ''
): AppCustomRouteRecordRaw[] {
  // 对该级菜单按sort升序排序
  const sortedMenus = sortMenus(menus)

  return sortedMenus.map((menu) => {
    // 构建当前菜单的完整组件路径
    let fullComponentPath = ''
    if (level === 0) {
      // 一级菜单直接使用自己的component
      fullComponentPath = menu.component
    } else {
      // 二级及以下菜单需要拼接父级路径
      fullComponentPath = `${parentComponentPath}/${menu.component}/${menu.component}`
    }

    // 转换为 PascalCase 格式
    const pascalCaseComponentPath = convertToPascalCasePath(fullComponentPath)

    const route: AppCustomRouteRecordRaw = {
      path: menu.component, // 使用component作为path
      component: hasChild(menu) && level === 0 ? '#' : `views/${pascalCaseComponentPath}`,
      redirect:
        level === 0 && hasChild(menu) ? `${menu.component}/${menu?.children?.[0]?.component}` : '',
      name: compToName(pascalCaseComponentPath),
      meta: {
        title: menu.name,
        icon: menu.icon || undefined, // 只有一级菜单有图标
        alwaysShow: level === 0 // 一级菜单必须有alwaysShow: true
        // permission: ['edit', 'add', 'delete'] // 添加按钮权限
      },
      children: []
    }
    // 二级菜单的处理
    if (level === 1 && menu.children && menu.children.length > 0) {
      const permission: string[] = getBtnPermission(menu.children)
      route.meta.permission = permission
      // 生成之后删除 children 字段
      delete menu.children
    }

    // 处理子菜单
    if (menu.children && menu.children.length > 0 && level <= 1) {
      route.children = transformMenuToRoute(menu.children, level + 1, fullComponentPath)
    }

    // 如果没有子菜单，删除 children 字段
    if (!route.children || route.children.length === 0) {
      delete route.children
    }

    return route
  })
}
